import type { TranscriptionRequest } from '@recipes/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import {
  browserRecognitionAvailable,
  createBrowserRecognizer,
  createServerRecognizer,
  mediaRecorderAvailable,
  type Recognizer,
} from '@/lib/speech/recognition';
import { SilenceDetector } from '@/lib/speech/silence';

export type ListeningState = 'idle' | 'listening' | 'processing' | 'error';

export interface PushToTalkOptions {
  language: 'de' | 'en';
  /** Profile preference: skip the browser adapter and always transcribe on the server. */
  preferServerTranscription: boolean;
  /** Server transcription is only offered when the feature is enabled and we are online. */
  serverTranscriptionAvailable: boolean;
  context: TranscriptionRequest['context'];
  onResult: (transcript: string) => void;
  onError?: (error: Error) => void;
  /**
   * The recording ends by itself (docs/partial/shopping-in-store-spec.md 6.1). Not set: it ends
   * when `stop()` is called, as in the cooking mode and the tasting notes.
   */
  autoStop?: AutoStopOptions;
}

export type DictationEndReason = 'tap' | 'silence' | 'word' | 'cancel-word' | 'max' | 'nothing';

export interface AutoStopOptions {
  /** Silence after the last word that ends the recording. */
  silenceMs: number;
  /** Patience while nothing was said at all. */
  initialSilenceMs: number;
  maxMs: number;
  /** Looks for a spoken command at the end of the transcript. */
  detectEnd: (transcript: string) => { action: 'finish' | 'cancel' | null; text: string };
  /** Called once per recording, also when nothing was understood or it was discarded. */
  onEnd?: (end: { reason: DictationEndReason; text: string }) => void;
  /** While this is true (the button is held down) a pause does not end the recording. */
  isHeld?: () => boolean;
}

export interface PushToTalk {
  state: ListeningState;
  transcript: string;
  /** Which adapter will be used; null when speech input is unavailable. */
  adapter: 'browser' | 'server' | null;
  /** Microphone level 0–1 while recording with the server adapter; null otherwise. */
  level: number | null;
  /** Speech was heard and it is quiet: the recording ends at `endsAt` unless someone speaks. */
  silence: { endsAt: number; totalMs: number } | null;
  start: () => void;
  stop: () => void;
  cancel: () => void;
}

/**
 * Hold-to-talk: `start()` on pointer/key down, `stop()` on release. The browser adapter
 * streams interim text; the server adapter records and transcribes after release.
 */
export function usePushToTalk(options: PushToTalkOptions): PushToTalk {
  const [state, setState] = useState<ListeningState>('idle');
  const [transcript, setTranscript] = useState('');
  const [level, setLevel] = useState<number | null>(null);
  const [silence, setSilence] = useState<PushToTalk['silence']>(null);
  const recognizer = useRef<Recognizer | null>(null);
  const timers = useRef<{
    silence?: ReturnType<typeof setTimeout>;
    max?: ReturnType<typeof setTimeout>;
  }>({});
  const endReason = useRef<DictationEndReason>('tap');
  const stopRef = useRef<() => void>(() => undefined);
  const cancelRef = useRef<() => void>(() => undefined);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const browserOk = browserRecognitionAvailable();
  const serverOk = options.serverTranscriptionAvailable && mediaRecorderAvailable();
  const adapter: PushToTalk['adapter'] =
    options.preferServerTranscription && serverOk
      ? 'server'
      : browserOk
        ? 'browser'
        : serverOk
          ? 'server'
          : null;

  const clearTimers = useCallback(() => {
    clearTimeout(timers.current.silence);
    clearTimeout(timers.current.max);
    timers.current = {};
    setSilence(null);
    setLevel(null);
  }, []);

  const fail = useCallback(
    (error: Error) => {
      clearTimers();
      recognizer.current = null;
      setState('error');
      optionsRef.current.onError?.(error);
    },
    [clearTimers],
  );

  const start = useCallback(() => {
    if (recognizer.current || !adapter) {
      if (!adapter) fail(new Error('SPEECH_UNAVAILABLE'));
      return;
    }
    setTranscript('');
    const autoStop = optionsRef.current.autoStop;
    endReason.current = 'tap';
    // Browser adapter: every result restarts the silence timer; the first one ends the grace period.
    const armSilence = (ms: number, reason: DictationEndReason, visible: boolean) => {
      clearTimeout(timers.current.silence);
      timers.current.silence = setTimeout(() => endBy(reason), ms);
      setSilence(visible ? { endsAt: Date.now() + ms, totalMs: ms } : null);
    };
    const endBy = (reason: DictationEndReason) => {
      if (endReason.current !== 'tap') return;
      // Held down (push-to-talk): a pause waits; the release ends the recording.
      if ((reason === 'silence' || reason === 'nothing') && autoStop?.isHeld?.()) {
        if (adapter === 'browser') armSilence(autoStop.silenceMs, 'silence', false);
        return;
      }
      endReason.current = reason;
      if (reason === 'cancel-word') cancelRef.current();
      else stopRef.current();
    };
    const detector =
      autoStop && adapter === 'server'
        ? new SilenceDetector({
            silenceMs: autoStop.silenceMs,
            initialSilenceMs: autoStop.initialSilenceMs,
          })
        : null;
    const handlers = {
      onInterim: (text: string) => {
        setTranscript(text);
        if (!autoStop || endReason.current !== 'tap') return;
        const end = autoStop.detectEnd(text);
        if (end.action) endBy(end.action === 'cancel' ? 'cancel-word' : 'word');
        else if (text) armSilence(autoStop.silenceMs, 'silence', true);
      },
      onLevel:
        detector && autoStop
          ? (rms: number) => {
              if (endReason.current !== 'tap') return;
              setLevel(Math.min(1, rms / Math.max(detector.threshold * 4, 0.05)));
              const verdict = detector.feed(rms, Date.now());
              if (verdict.state === 'end') endBy('silence');
              else if (verdict.state === 'nothing') endBy('nothing');
              else if (verdict.state === 'silence')
                setSilence(
                  (current) =>
                    current ?? {
                      endsAt: Date.now() + autoStop.silenceMs - verdict.silentForMs,
                      totalMs: autoStop.silenceMs,
                    },
                );
              else setSilence(null);
            }
          : undefined,
      onFinal: (raw: string) => {
        clearTimers();
        recognizer.current = null;
        // A trailing command is not an item – also when it did not end the recording itself
        // (server adapter: there is no text before the recording is over).
        const end = autoStop?.detectEnd(raw);
        if (end?.action === 'cancel') endReason.current = 'cancel-word';
        else if (end?.action === 'finish' && endReason.current === 'tap')
          endReason.current = 'word';
        const discarded = endReason.current === 'cancel-word';
        const text = discarded ? '' : (end?.text ?? raw);
        setTranscript(text);
        setState('idle');
        if (text) optionsRef.current.onResult(text);
        const reason = !text && !discarded ? 'nothing' : endReason.current;
        autoStop?.onEnd?.({ reason, text });
      },
      onError: fail,
    };
    try {
      const instance =
        adapter === 'browser'
          ? createBrowserRecognizer(optionsRef.current.language, handlers)
          : createServerRecognizer(
              {
                language: optionsRef.current.language,
                context: optionsRef.current.context,
                transcribe: (body) => api.speech.transcribe(body),
              },
              handlers,
            );
      recognizer.current = instance;
      setState('listening');
      if (autoStop) {
        timers.current.max = setTimeout(() => endBy('max'), autoStop.maxMs);
        if (adapter === 'browser') armSilence(autoStop.initialSilenceMs, 'nothing', false);
      }
      instance.start().catch(fail);
    } catch (error) {
      fail(error instanceof Error ? error : new Error(String(error)));
    }
  }, [adapter, clearTimers, fail]);

  const stop = useCallback(() => {
    const current = recognizer.current;
    if (!current) return;
    clearTimers();
    setState('processing');
    void current.stop().then(() => {
      // The final result handler moves the state back to idle; make sure we never get stuck.
      setState((s) => (s === 'processing' ? 'idle' : s));
      recognizer.current = null;
    });
  }, [clearTimers]);

  const cancel = useCallback(() => {
    const wasRecording = recognizer.current !== null;
    clearTimers();
    recognizer.current?.abort();
    recognizer.current = null;
    setTranscript('');
    setState('idle');
    // Discarded by a spoken word: the caller still hears about the end of the recording.
    if (wasRecording && endReason.current === 'cancel-word')
      optionsRef.current.autoStop?.onEnd?.({ reason: 'cancel-word', text: '' });
  }, [clearTimers]);
  stopRef.current = stop;
  cancelRef.current = cancel;

  useEffect(
    () => () => {
      clearTimeout(timers.current.silence);
      clearTimeout(timers.current.max);
      recognizer.current?.abort();
    },
    [],
  );

  return { state, transcript, adapter, level, silence, start, stop, cancel };
}
